'use client';

/**
 * ReplaceTable — shared "consolidation" section used by:
 *   - home/WhatSumReplaces.tsx ("What SUM consolidates")
 *   - features/Section1.tsx ("What's in the box")
 *
 * Renders a `.sum-replace-row` table with category name, struck-through
 * competitor tool list, outcome text, and an "Included" badge per row.
 * Each consumer page supplies its own rows + section copy; the visual
 * pattern (and any future evolution of it) lives here.
 *
 * Page-specific copy stays in the calling component — kicker, headline,
 * subhead, "Included" label, and the row contents — because both pages
 * intentionally lead with different framings (consolidation vs. day-one
 * readiness) even though the underlying mechanic is the same.
 */

import React from 'react';
import SectionLabel from '../../home/SectionLabel';

export type ReplaceRow = {
  key: string;
  cat: string;
  tools: string;
  feature: React.ReactNode;
  flagship?: boolean;
  badge?: string;
};

type Props = {
  num: string;
  label: string;
  headlineA: string;
  headlineB: string;
  subhead?: string;
  rows: ReplaceRow[];
  includedLabel: string;
  replacesPrefix?: string;
  summary?: string;
  /** Anchor id, e.g. for in-page links from a hero CTA. */
  id?: string;
};

const ReplaceTable = ({
  num,
  label,
  headlineA,
  headlineB,
  subhead,
  rows,
  includedLabel,
  replacesPrefix,
  summary,
  id,
}: Props) => {
  return (
    <section
      id={id}
      className="px-6 py-24 lg:py-28 scroll-mt-20"
      style={{
        background: '#0A0E27',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num={num} label={label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {headlineA} <span style={{ color: '#B4C1D8' }}>{headlineB}</span>
        </h2>
        {subhead && (
          <p
            style={{
              color: '#B4C1D8',
              fontSize: 18,
              maxWidth: 720,
              lineHeight: 1.5,
              margin: '0 0 48px',
            }}
          >
            {subhead}
          </p>
        )}

        {summary && (
          <div className="sum-replace-summary mono" aria-hidden="true">
            {summary}
          </div>
        )}

        <div className="sum-replace-table">
          {rows.map((row) => {
            const className = ['sum-replace-row', row.flagship && 'is-flagship', row.badge && 'is-badged']
              .filter(Boolean)
              .join(' ');
            return (
              <div key={row.key} className={className}>
                <div className="sum-replace-cat">
                  <div className="sum-replace-name">{row.cat}</div>
                  {row.badge && <span className="sum-replace-flag mono">{row.badge}</span>}
                </div>
                <div className="sum-replace-tools">
                  {replacesPrefix && <span className="sum-replace-tools-prefix mono">{replacesPrefix}</span>}
                  <span className="sum-replace-tools-list">{row.tools}</span>
                </div>
                <div className="sum-replace-feature">{row.feature}</div>
                {!summary && <div className="sum-replace-badge">{includedLabel}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ReplaceTable;
